import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeGet, executePatch } from '@/services/lukita'
import { PROJECT_TASK_STATUSES } from '../constants'
import type { ProjectTask, ProjectTaskDTO } from './types'

interface TasksStoreActions {
  reset: () => void
  setTasks: (tasks: ProjectTaskDTO[]) => void
  replaceTask: (task: ProjectTask) => void
}

interface TasksStoreLoaders {
  fetchTasks: (slug: string) => Promise<void>
}

interface TasksStoreMutations {
  toggleTask: (slug: string, task: ProjectTask) => Promise<void>
}

interface TasksStore {
  loadingTasks: boolean
  tasks: ProjectTask[]
  actions: TasksStoreActions
  loaders: TasksStoreLoaders
  mutations: TasksStoreMutations
}

export const useTasksStore = create<TasksStore>((set, get, store) => ({
  loadingTasks: true,
  tasks: [],
  actions: {
    reset: () => set(store.getInitialState()),
    setTasks: (tasks) =>
      set({
        tasks: camelcaseKeys(tasks) as ProjectTask[],
        loadingTasks: false,
      }),
    replaceTask: (task) =>
      set((state) => ({
        tasks: state.tasks.map((row) => (row.id === task.id ? task : row)),
      })),
  },
  loaders: {
    fetchTasks: async (slug) => {
      set({ loadingTasks: true })
      const response = await executeGet(`/projects/${slug}/tasks`)
      get().actions.setTasks(await response.json())
    },
  },
  mutations: {
    toggleTask: async (slug, task) => {
      const status =
        task.status === PROJECT_TASK_STATUSES.done
          ? PROJECT_TASK_STATUSES.open
          : PROJECT_TASK_STATUSES.done
      const { replaceTask } = get().actions

      replaceTask({ ...task, status })
      try {
        await executePatch(`/projects/${slug}/tasks/${task.id}`, {
          task: { status: status.toLowerCase() },
        })
      } catch (error) {
        replaceTask(task)
        throw error
      }
    },
  },
}))
